import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export interface Lesson {
  id: string;
  title: string;
  description: string | null;
  duration: string | null;
  price: number;
  regular_price: number;
  is_free: boolean;
  cover_url: string | null;
  available_from: string | null;
  available_until: string | null;
}

const LESSON_COLUMNS = "id, title, description, duration, price, regular_price, is_free, cover_url, available_from, available_until";

export const useLessons = () => {
  return useQuery({
    queryKey: ["lessons", "published"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("lessons")
        .select(LESSON_COLUMNS)
        .eq("is_published", true)
        .order("available_until", { ascending: true });
      if (error) throw error;
      return (data ?? []).map((l: any) => ({
        ...l,
        price: l.price ?? 0,
        regular_price: l.regular_price ?? 49.9,
        is_free: l.is_free ?? false,
      })) as Lesson[];
    },
  });
};

export const useActiveLesson = () => {
  return useQuery({
    queryKey: ["lessons", "active"],
    queryFn: async () => {
      const nowIso = new Date().toISOString();
      // 1) Prefer a time-bounded active sale
      const { data: timed, error: timedErr } = await supabase
        .from("lessons")
        .select("id, title, description, duration, price, regular_price, is_free, cover_url, available_until")
        .eq("is_published", true)
        .not("available_until", "is", null)
        .gte("available_until", nowIso)
        .order("available_until", { ascending: true })
        .limit(1);
      if (timedErr) throw timedErr;
      if (timed && timed.length > 0) {
        const d = timed[0] as any;
        return { ...d, regular_price: d.regular_price ?? 49.9 };
      }

      // 2) Fallback: any published lesson currently priced below its regular price
      const { data: discounted, error: discErr } = await supabase
        .from("lessons")
        .select("id, title, description, duration, price, regular_price, is_free, cover_url, available_until")
        .eq("is_published", true)
        .not("regular_price", "is", null)
        .order("price", { ascending: true })
        .limit(10);
      if (discErr) throw discErr;
      const sale = (discounted ?? []).find(
        (l: any) => Number(l.price) > 0 && Number(l.price) < Number(l.regular_price ?? 49.9)
      );
      if (!sale) return null;
      return { ...(sale as any), regular_price: (sale as any).regular_price ?? 49.9 };
    },
    staleTime: 60 * 1000,
  });
};
